import React, { Component, ErrorInfo, ReactNode } from 'react'
import { Alert, Button, Box, Typography } from '@mui/material'
import ReplayIcon from '@mui/icons-material/Replay'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

/**
 * LiveKitErrorBoundary - A React error boundary specifically for LiveKit components
 *
 * This component catches errors in LiveKit components and provides a UI for recovery
 * without crashing the entire application.
 */
class LiveKitErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    }
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null,
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log the error to console
    console.error('LiveKit component error:', error, errorInfo)

    // Update state to include the error details
    this.setState({
      errorInfo,
    })
  }

  handleRetry = (): void => {
    // Attempt to reset the error state
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    })

    // Trigger LiveKit reconnection
    window.dispatchEvent(new CustomEvent('livekit-manual-reconnect'))

    // Also reload device permissions
    window.dispatchEvent(new CustomEvent('livekit-manual-device-reconnect'))
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 2, maxWidth: '100%' }}>
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={this.handleRetry}
                startIcon={<ReplayIcon />}
              >
                Retry Connection
              </Button>
            }
          >
            <Typography variant="h6">Video Connection Error</Typography>
            <Typography variant="body2">
              There was a problem with the video connection. This could be due to network issues or
              permission problems.
            </Typography>
          </Alert>
        </Box>
      )
    }

    // If no error, render children normally
    return this.props.children
  }
}

export default LiveKitErrorBoundary
